import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { signIn, signUp } from '../supabase/api';

const { FiTarget, FiMail, FiLock, FiUser, FiEye, FiEyeOff, FiAlertCircle, FiCheckCircle } = FiIcons;

function Auth({ onAuth }) {
  const [mode, setMode] = useState('signin');
  const [role, setRole] = useState('disciple'); // Keeps underlying DB role value the same
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [form, setForm] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: ''
  });

  const set = (field) => (e) => setForm((p) => ({ ...p, [field]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (mode === 'signup') {
      if (!form.name.trim()) return setError('Please enter your name.');
      if (form.password.length < 6) return setError('Password must be at least 6 characters.');
      if (form.password !== form.confirmPassword) return setError('Passwords do not match.');
    }

    setLoading(true);
    try {
      if (mode === 'signup') {
        const res = await signUp(form.email, form.password, role, form.name);
        
        // If there's no session returned, email confirmation is required
        if (!res.session) {
          setSuccess('Account created! Please check your email to confirm your account before signing in.');
          setMode('signin');
          setForm((p) => ({ ...p, password: '', confirmPassword: '' }));
          setLoading(false);
          return;
        }
      } else {
        await signIn(form.email, form.password);
      }
      onAuth();
    } catch (err) {
      setError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const switchMode = () => {
    setMode((m) => (m === 'signin' ? 'signup' : 'signin'));
    setError('');
    setSuccess('');
    setForm({ name: '', email: '', password: '', confirmPassword: '' });
  };

  return (
    <div className="min-h-screen bg-secondary dark:bg-gray-900 flex items-center justify-center px-4 transition-colors duration-200">
      <div className="w-full max-w-md">
        <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} className="text-center mb-8">
          <div className="w-16 h-16 bg-primary rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
            <SafeIcon icon={FiTarget} className="w-9 h-9 text-white" />
          </div>
          <h1 className="text-3xl font-space font-bold text-gray-900 dark:text-white">DisciplePath</h1>
          <p className="text-gray-500 dark:text-gray-400 mt-1">Together in Christ</p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 p-8 transition-colors duration-200"
        >
          <div className="flex bg-gray-100 dark:bg-gray-700 rounded-xl p-1 mb-8">
            {['signin', 'signup'].map((m) => (
              <button
                key={m}
                onClick={() => { setMode(m); setError(''); setSuccess(''); }}
                className={`flex-1 py-2.5 rounded-lg text-sm font-medium transition-all ${
                  mode === m
                    ? 'bg-white dark:bg-gray-800 text-gray-900 dark:text-white shadow-sm'
                    : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
                }`}
              >
                {m === 'signin' ? 'Sign In' : 'Create Account'}
              </button>
            ))}
          </div>

          {success && (
            <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} className="mb-6 flex items-start gap-2.5 p-3 bg-green-50 dark:bg-green-900/30 border border-green-100 dark:border-green-800 rounded-xl">
              <SafeIcon icon={FiCheckCircle} className="w-4 h-4 text-green-500 dark:text-green-400 mt-0.5 flex-shrink-0" />
              <p className="text-sm text-green-600 dark:text-green-300">{success}</p>
            </motion.div>
          )}

          <AnimatePresence mode="wait">
            <motion.form
              key={mode}
              initial={{ opacity: 0, x: mode === 'signup' ? 20 : -20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              onSubmit={handleSubmit}
              className="space-y-5"
            >
              {mode === 'signup' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">Full Name</label>
                  <div className="relative">
                    <SafeIcon icon={FiUser} className="absolute left-3 top-3.5 w-4 h-4 text-gray-400" />
                    <input
                      type="text"
                      value={form.name}
                      onChange={set('name')}
                      placeholder="Your full name"
                      required
                      className="w-full pl-10 pr-4 py-3 border border-gray-200 dark:border-gray-600 bg-transparent dark:bg-gray-900 dark:text-white dark:placeholder-gray-500 rounded-xl custom-focus text-sm transition-colors"
                    />
                  </div>
                </div>
              )}

              {mode === 'signup' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">I am joining as a…</label>
                  <div className="flex gap-3">
                    {['leader', 'disciple'].map((r) => (
                      <button
                        key={r}
                        type="button"
                        onClick={() => setRole(r)}
                        className={`flex-1 py-3 rounded-xl border-2 text-sm font-medium transition-all ${
                          role === r
                            ? 'border-primary bg-primary text-white'
                            : 'border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:border-primary dark:hover:border-primary hover:text-primary dark:hover:text-primary'
                        }`}
                      >
                        {r === 'disciple' ? 'Participant' : 'Leader'}
                      </button>
                    ))}
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                    {role === 'disciple'
                      ? '✓ Your leader will link you to their account after you sign up.'
                      : '✓ After signing up, go to Profile to link your participant by their email.'}
                  </p>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">Email Address</label>
                <div className="relative">
                  <SafeIcon icon={FiMail} className="absolute left-3 top-3.5 w-4 h-4 text-gray-400" />
                  <input
                    type="email"
                    value={form.email}
                    onChange={set('email')}
                    placeholder="you@example.com"
                    required
                    className="w-full pl-10 pr-4 py-3 border border-gray-200 dark:border-gray-600 bg-transparent dark:bg-gray-900 dark:text-white dark:placeholder-gray-500 rounded-xl custom-focus text-sm transition-colors"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">Password</label>
                <div className="relative">
                  <SafeIcon icon={FiLock} className="absolute left-3 top-3.5 w-4 h-4 text-gray-400" />
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={form.password}
                    onChange={set('password')}
                    placeholder={mode === 'signup' ? 'Min. 6 characters' : 'Your password'}
                    required
                    className="w-full pl-10 pr-10 py-3 border border-gray-200 dark:border-gray-600 bg-transparent dark:bg-gray-900 dark:text-white dark:placeholder-gray-500 rounded-xl custom-focus text-sm transition-colors"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((v) => !v)}
                    className="absolute right-3 top-3.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                  >
                    <SafeIcon icon={showPassword ? FiEyeOff : FiEye} className="w-4 h-4" />
                  </button>
                </div>
              </div>

              {mode === 'signup' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">Confirm Password</label>
                  <div className="relative">
                    <SafeIcon icon={FiLock} className="absolute left-3 top-3.5 w-4 h-4 text-gray-400" />
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={form.confirmPassword}
                      onChange={set('confirmPassword')}
                      placeholder="Repeat your password"
                      required
                      className="w-full pl-10 pr-4 py-3 border border-gray-200 dark:border-gray-600 bg-transparent dark:bg-gray-900 dark:text-white dark:placeholder-gray-500 rounded-xl custom-focus text-sm transition-colors"
                    />
                  </div>
                </div>
              )}

              {error && (
                <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} className="flex items-start gap-2.5 p-3 bg-red-50 dark:bg-red-900/30 border border-red-100 dark:border-red-800 rounded-xl">
                  <SafeIcon icon={FiAlertCircle} className="w-4 h-4 text-red-500 dark:text-red-400 mt-0.5 flex-shrink-0" />
                  <p className="text-sm text-red-600 dark:text-red-300">{error}</p>
                </motion.div>
              )}

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-primary text-white py-3 rounded-xl font-medium hover:bg-primary-dark transition-colors disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {loading ? <span className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" /> : mode === 'signin' ? 'Sign In' : 'Create Account'}
              </button>
            </motion.form>
          </AnimatePresence>

          <p className="text-center text-sm text-gray-500 dark:text-gray-400 mt-6">
            {mode === 'signin' ? "Don't have an account?" : 'Already have an account?'}
            {' '}
            <button onClick={switchMode} className="text-primary font-medium hover:underline">
              {mode === 'signin' ? 'Sign up' : 'Sign in'}
            </button>
          </p>
        </motion.div>
      </div>
    </div>
  );
}

export default Auth;